import * as THREE from "three";
import { Line, Polygon, Solid, Vector3 } from "opengeometry";

import { IShape } from "../../shapes/base-type";
import { ElementType } from "../base-type";
import { OPGeometry, IPlaceable } from "../op-element";
import {
  WallMaterial,
  WallOptions
} from "./wall-types";
import { Opening } from "../openings/opening";
import { Door, Window } from "../openings";
import { WallFrame, computeWallFrame } from "./wall-frame";
import { Event } from "../../../../../utils/event";
import type { Vec3 } from "../../types";
import { rotatePoints, degToRad, Y_UP, centroid as centroidOf } from "../../../../../utils/transform";
export { WallMaterial } from "./wall-types";

export class SingleWall extends Line implements IShape, IPlaceable {
  ogType = ElementType.WALL;

  subElements2D: Map<string, OPGeometry> = new Map();
  private isProfileView: boolean = true;

  subElements3D: Map<string, OPGeometry> = new Map();
  private isModelView: boolean = true;

  private openings: Opening[] = [];
  private _frame: WallFrame | null = null;
  private _wallOutlineVertices: Vector3[] = [];

  /** Fires whenever the wall's frame changes (endpoints, thickness, height). */
  onWallGeometryChanged: Event<null> = new Event();

  selected = false;
  edit = false;
  locked = false;

  /** Whether to keep this as pure line and not convert to 2D and 3D meshes. 
   * This can be used to create a Wall Engine, that will use the line as the base, and generate 2D and 3D geometry as needed, and keep them in sync with the line geometry.
   * If enabled, we will not generate 2D and 3D geometry for this wall, but keep it as a line, and let the Wall Engine handle the geometry generation and syncing.
  */
  isLineWall: boolean = false;

  // UI Properties
  _outlineEnabled: boolean = false;

  // Semantic Properties
  propertySet: WallOptions = {
    labelName: "Standard Wall",
    thickness: 0.3,
    material: WallMaterial.CONCRETE,
    color: 0xcccccc,
    height: 3,
    points: [
      [0, 0, 0],
      [1.5, 0, 1.5],
    ],
    openings: [],
  };

  move(delta: Vec3): void {
    const [s, e] = this.propertySet.points;
    this.propertySet.points = [
      [s[0] + delta[0], s[1] + delta[1], s[2] + delta[2]],
      [e[0] + delta[0], e[1] + delta[1], e[2] + delta[2]],
    ];
    this.setOPGeometry();
  }

  rotate(angleDeg: number, axis: Vec3 = Y_UP, anchor?: Vec3): void {
    const pivot = anchor ?? centroidOf(this.propertySet.points as Vec3[]);
    const rotated = rotatePoints(
      this.propertySet.points as Vec3[],
      degToRad(angleDeg),
      axis,
      pivot,
    );
    this.propertySet.points = [rotated[0], rotated[1]];
    this.setOPGeometry();
  }

  get outline() { return this._outlineEnabled; }
  set outline(value: boolean) {
    this._outlineEnabled = value;
    for (const obj of this.subElements2D.values()) {
      obj.outline = value;
    }
    for (const obj of this.subElements3D.values()) {
      obj.outline = value;
    }
  }

  get labelName() { return this.propertySet.labelName; }
  set labelName(value: string) {
    this.propertySet.labelName = value;
  }

  get wallThickness() { return this.propertySet.thickness; }
  set wallThickness(value: number) {
    this.propertySet.thickness = Math.max(0.1, value);
    this.setOPGeometry();
  }

  get wallMaterial() { return this.propertySet.material; }
  set wallMaterial(value: WallMaterial | string) {
    this.propertySet.material = value;
  }

  get wallColor() { return this.propertySet.color; }
  set wallColor(value: number) {
    this.propertySet.color = value;
    this.setOPMaterial();
  }

  get wallHeight() { return this.propertySet.height; }
  set wallHeight(value: number) {
    this.propertySet.height = Math.max(0.1, value);
    this.setOPGeometry();
  }

  get start() { return this.propertySet.points[0]; }
  set start(value: [number, number, number]) {
    if (!this.distanceChecker(value, this.end)) {
      return;
    }
    this.propertySet.points[0] = value;
    this.setOPGeometry();
  }

  get end() { return this.propertySet.points[1]; }
  set end(value: [number, number, number]) {
    if (!this.distanceChecker(this.start, value)) {
      return;
    }
    this.propertySet.points[1] = value;
    this.setOPGeometry();
  }

  /**
   * Checks if the length of wall is not negative or zero, and if the start and end points are not the same. If invalid, it throws an error. This can be used in the UI to prevent users from creating walls with invalid geometry.
   * @param start 
   * @param end 
   * @returns boolean indicating whether the distance is valid or not
   */
  distanceChecker(start: [number, number, number], end: [number, number, number]): boolean {
    const startVec = new Vector3(start[0], start[1], start[2]);
    const endVec = new Vector3(end[0], end[1], end[2]);
    const distance = startVec.subtract(endVec).length();
    if (distance <= 0) {
      console.error("Invalid wall geometry: Start and end points cannot be the same or have zero length.");
      return false;
    }
    return true;
  }

  get profileView() { return this.isProfileView; }
  set profileView(value: boolean) {
    this.isProfileView = value;
    const wall2D = this.subElements2D.get(this.ogid + '-2d');
    if (wall2D) wall2D.visible = value;
  }

  get modelView() { return this.isModelView; }
  set modelView(value: boolean) {
    this.isModelView = value;

    if (this.subElements3D.has(this.ogid + '-3d-resolved')) {
      const resolved3D = this.subElements3D.get(this.ogid + '-3d-resolved') as Solid;
      resolved3D.visible = value;
      return;
    }

    for (const obj of this.subElements3D.values()) {
      obj.visible = value;
    }
  }

  constructor(wallConfig?: Partial<WallOptions>) {
    super({
      start: new Vector3(0, 0, 0),
      end: new Vector3(1.5, 0, 1.5),
      color: 0xcccccc,
    });

    this.subElements2D = new Map<string, THREE.Object3D>();
    this.subElements3D = new Map<string, THREE.Object3D>();
    
    if (wallConfig) {
      this.propertySet = {
        ...this.propertySet,
        ...wallConfig
      };
    }

    this.propertySet.ogid = this.ogid;
    this.setOPGeometry();
  }

  setOPConfig(config: WallOptions): void {
    this.dispose();

    this.propertySet = {
      ...this.propertySet,
      ...config,
    };

    this.setOPGeometry();
    this.setOPMaterial();
  }

  getOPConfig(): WallOptions {
    return this.propertySet;
  }

  /** Returns this wall's local coordinate frame, computing on demand. */
  getFrame(): WallFrame {
    if (!this._frame) {
      const [s, e] = this.propertySet.points;
      this._frame = computeWallFrame(
        new Vector3(s[0], s[1], s[2]),
        new Vector3(e[0], e[1], e[2]),
        this.propertySet.thickness,
      );
    }
    return this._frame;
  }

  attachDoor(doorElement: Door) {
    doorElement.hostWallId = this.ogid;
    doorElement.bindHostFrame(this.getFrame());

    const openingFromDoor = doorElement.opening as Opening;
    if (!openingFromDoor) {
      console.error("Door element does not have a valid opening configuration.");
      return;
    }
    this.openings.push(openingFromDoor);
    this.propertySet.openings.push(openingFromDoor.ogid!);
    this.resolveOpenings();

    openingFromDoor.onOpeningUpdated.add(() => {
      this.resolveOpenings();
    });

    this.onWallGeometryChanged.add(() => {
      doorElement.bindHostFrame(this.getFrame());
    });
  }

  /**
   * Detach a previously attached door. Removes it from the openings list,
   * unbinds its host frame (so it falls back to the unhosted identity frame),
   * and re-resolves the wall's CSG so the cut hole is closed up.
   */
  detachDoor(doorElement: Door) {
    const openingFromDoor = doorElement.opening as Opening;
    const openingOgid = openingFromDoor?.ogid;

    const index = this.openings.findIndex((o) => o === openingFromDoor);
    if (index !== -1) this.openings.splice(index, 1);

    if (openingOgid) {
      const propIndex = this.propertySet.openings.indexOf(openingOgid);
      if (propIndex !== -1) this.propertySet.openings.splice(propIndex, 1);
    }

    doorElement.hostWallId = undefined;
    doorElement.bindHostFrame(null);

    this.resolveOpenings();
  }

  /**
   * Detach a previously attached window. Removes it from the openings list,
   * unbinds its host frame (so it falls back to the unhosted identity frame),
   * and re-resolves the wall's CSG so the cut hole is closed up.
   */
  detachWindow(windowElement: Window) {
    const openingFromWindow = windowElement.opening as Opening;
    const openingOgid = openingFromWindow?.ogid;

    const index = this.openings.findIndex((o) => o === openingFromWindow);
    if (index !== -1) this.openings.splice(index, 1);

    if (openingOgid) {
      const propIndex = this.propertySet.openings.indexOf(openingOgid);
      if (propIndex !== -1) this.propertySet.openings.splice(propIndex, 1);
    }

    windowElement.hostWallId = undefined;
    windowElement.bindHostFrame(null);

    this.resolveOpenings();
  }

  attachWindow(windowElement: Window) {
    windowElement.hostWallId = this.ogid;
    windowElement.bindHostFrame(this.getFrame());

    const openingFromWindow = windowElement.opening as Opening;
    if (!openingFromWindow) {
      console.error("Window element does not have a valid opening configuration.");
      return;
    }
    this.openings.push(openingFromWindow);
    this.propertySet.openings.push(openingFromWindow.ogid!);
    this.resolveOpenings();

    openingFromWindow.onOpeningUpdated.add(() => {
      this.resolveOpenings();
    });

    this.onWallGeometryChanged.add(() => {
      windowElement.bindHostFrame(this.getFrame());
    });
  }

  attachOpening(openingElement: Opening) {
    openingElement.bindHostFrame(this.getFrame());
    this.openings.push(openingElement);
    const openingConfig = openingElement.getOPConfig();
    // Add Opening to Wall's propertySet
    this.propertySet.openings.push(openingConfig.ogid!);
    this.resolveOpenings();

    openingElement.onOpeningUpdated.add(() => {
      this.resolveOpenings();
    });

    this.onWallGeometryChanged.add(() => {
      openingElement.bindHostFrame(this.getFrame());
    });
  }

  resolveOpenings() {
    // ── 2D: polygon-with-holes rebuild ───────────────────────────────────────
    if (this._wallOutlineVertices.length >= 3) {
      const existing2D = this.subElements2D.get(this.ogid + '-2d');
      if (existing2D) {
        const mesh = existing2D as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((m) => (m as THREE.Material).dispose());
        } else if (mesh.material) {
          (mesh.material as THREE.Material).dispose();
        }
        existing2D.removeFromParent();
        this.subElements2D.delete(this.ogid + '-2d');
      }

      const holeLoops = this.openings
        .map((o) => o.holeLoop2D)
        .filter((loop): loop is Vector3[] => loop.length >= 3);

      const wall2D = new Polygon({
        vertices: this._wallOutlineVertices.map((v) => v.clone()),
        holes: holeLoops,
        color: this.propertySet.color,
      });
      wall2D.outline = this._outlineEnabled;
      wall2D.visible = this.isProfileView;
      this.subElements2D.set(this.ogid + '-2d', wall2D);
      this.add(wall2D);
    }

    // ── 3D: unchanged boolean subtract ───────────────────────────────────────
    const wall3D = this.subElements3D.get(this.ogid + "-3d-base") as Solid | undefined;
    if (!wall3D) return;

    const resolved3DKey = this.ogid + "-3d-resolved";
    const existingResolved3D = this.subElements3D.get(resolved3DKey);
    if (existingResolved3D) {
      existingResolved3D.removeFromParent();
      this.subElements3D.delete(resolved3DKey);
    }

    const all3DOpenings = this.openings
      .map((opening) => opening.opening3D)
      .filter((opening): opening is Solid => Boolean(opening));

    if (all3DOpenings.length === 0) {
      wall3D.visible = this.isModelView;
      return;
    }

    const result3D = wall3D.subtract(all3DOpenings, {
      color: this.propertySet.color,
    });

    wall3D.visible = false;
    all3DOpenings.forEach((opening3D) => {
      opening3D.visible = false;
    });

    this.subElements3D.set(resolved3DKey, result3D);
    this.add(result3D);
  }


  detachOpening(ogid: string) {
    const index = this.openings.findIndex((opening) => opening.ogid === ogid);
    if (index !== -1) this.openings.splice(index, 1);
    const propIndex = this.propertySet.openings.indexOf(ogid);
    if (propIndex !== -1) this.propertySet.openings.splice(propIndex, 1);
    this.resolveOpenings();
  }

  getHostedOpenings() {
    return this.openings;
  }

  dispose() {
    for (const obj of this.subElements2D.values()) {
      const mesh = obj as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach(mat => mat.dispose());
      } else if (mesh.material) {
        mesh.material.dispose();
      }

      mesh.removeFromParent();
    }
    for (const obj of this.subElements3D.values()) {
      const mesh = obj as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach(mat => mat.dispose());
      } else if (mesh.material) {
        mesh.material.dispose();
      }

      mesh.removeFromParent();
    }

    this.subElements2D.clear();
    this.subElements3D.clear();
    this.discardGeometry();
  }

  setOPGeometry(): void {
    this.dispose();

    const [s, e] = this.propertySet.points;
    const startVec = new Vector3(s[0], s[1], s[2]);
    const endVec = new Vector3(e[0], e[1], e[2]);

    this.setConfig({
      start: startVec,
      end: endVec,
      color: this.propertySet.color,
    });

    this._frame = computeWallFrame(startVec, endVec, this.propertySet.thickness);
    this.onWallGeometryChanged.trigger(null);

    if (this.isLineWall) {
      return;
    }

    const offset1 = this.getOffset(this.propertySet.thickness / 2);
    const offset2 = this.getOffset(-this.propertySet.thickness / 2);

    // Use loop later
    const points: Vector3[] = [
      // Start point with positive offset
      offset1.points[0].clone(),
      // End point with positive offset
      offset1.points[1].clone(),
      // End point with negative offset
      offset2.points[1].clone(),
      // Start point with negative offset
      offset2.points[0].clone(),
    ];

    // Store wall outline for resolveOpenings() to build the polygon-with-holes.
    this._wallOutlineVertices = points.map((v) => v.clone());

    // Base polygon: extrusion seed for the 3D path; not directly rendered (resolveOpenings builds '-2d').
    const polygon = new Polygon({
      ogid: this.ogid + '-2d-base',
      vertices: points,
      color: this.propertySet.color,
    });
    polygon.visible = false;
    this.subElements2D.set(polygon.ogid, polygon);
    this.add(polygon);

    // 3D Extrusion
    const height = this.propertySet.height; // Default height if not set
    const extrudedShape = polygon.extrude(height);
    extrudedShape.ogid = this.ogid + '-3d-base';
    const material = extrudedShape.material as THREE.MeshStandardMaterial;
    material.transparent = false;
    material.opacity = 1;
    material.color = new THREE.Color(this.propertySet.color);
    
    this.subElements3D.set(extrudedShape.ogid, extrudedShape);
    this.add(extrudedShape);

    // Resolve Openings after geometry is generated so that we can use the generated geometry for boolean operations
    this.resolveOpenings();

    // Retaining same outline and visibility settings for new geometry
    this.outline = this._outlineEnabled;
    this.profileView = this.isProfileView;
    this.modelView = this.isModelView;
  }

  create3D() {

  }

  setOPMaterial(): void {
    
  }
}
